import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import "./Select.css";
import { AUTO_FOCUS } from "../core/device";

const norm = (s) =>
  String(s || "").replace(/[يى]/g, "ی").replace(/ك/g, "ک")
    .replace(/\u200c/g, " ").replace(/\s+/g, " ").trim().toLowerCase();

export default function Select({
  value, onChange, options = [], placeholder = "انتخاب کنید...",
  disabled, searchable, id, emptyText = "موردی یافت نشد",
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [hi, setHi] = useState(-1);
  const [up, setUp] = useState(false);
  const root = useRef(null);
  const listRef = useRef(null);

  const selected = options.find((o) => String(o.value) === String(value));
  const shown = useMemo(() => {
    const s = norm(q);
    return s ? options.filter((o) => norm(o.label).includes(s)) : options;
  }, [options, q]);

  const close = () => { setOpen(false); setQ(""); };

  const toggle = () => {
    if (disabled) return;
    if (open) return close();
    setHi(Math.max(0, options.findIndex((o) => String(o.value) === String(value))));
    setOpen(true);
  };

  const pick = (o) => { onChange(o.value); close(); };

  // بستن با کلیک بیرون
  useEffect(() => {
    if (!open) return undefined;
    const h = (e) => { if (!root.current?.contains(e.target)) close(); };
    document.addEventListener("mousedown", h);
    document.addEventListener("touchstart", h);
    return () => {
      document.removeEventListener("mousedown", h);
      document.removeEventListener("touchstart", h);
    };
  }, [open]);

  // اگر پایین صفحه جا نبود، به بالا باز شود
  useLayoutEffect(() => {
    if (!open || !root.current) return;
    const r = root.current.getBoundingClientRect();
    const below = window.innerHeight - r.bottom;
    setUp(below < 290 && r.top > below);
  }, [open]);

  useEffect(() => {
    if (open && hi >= 0) listRef.current?.children[hi]?.scrollIntoView({ block: "nearest" });
  }, [hi, open]);

  const onKey = (e) => {
    if (disabled) return;
    if (!open) {
      if (["ArrowDown", "ArrowUp", "Enter", " "].includes(e.key) && e.target.tagName !== "INPUT") {
        e.preventDefault();
        toggle();
      }
      return;
    }
    if (e.key === "Escape") { e.preventDefault(); close(); }
    else if (e.key === "ArrowDown") { e.preventDefault(); setHi((i) => Math.min(shown.length - 1, i + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHi((i) => Math.max(0, i - 1)); }
    else if (e.key === "Enter") { e.preventDefault(); if (shown[hi]) pick(shown[hi]); }
  };

  return (
    <div className={`sel ${open ? "open" : ""} ${disabled ? "dis" : ""}`} ref={root} onKeyDown={onKey}>
      <button type="button" id={id} className="sel-btn" disabled={disabled}
        onClick={toggle} aria-haspopup="listbox" aria-expanded={open}>
        <span className={selected ? "" : "ph"}>{selected ? selected.label : placeholder}</span>
        <i className="fa-solid fa-chevron-down sel-caret" />
      </button>

      {open && <div className="sel-backdrop" onClick={close} />}
      {open && (
        <div className={`sel-pop ${up ? "up" : ""}`}>
          {searchable && (
            <div className="sel-search">
              <input autoFocus={AUTO_FOCUS} value={q} placeholder="جستجو..."
                onChange={(e) => { setQ(e.target.value); setHi(0); }} />
            </div>
          )}
          <ul role="listbox" ref={listRef}>
            {shown.length === 0 ? (
              <li className="sel-empty">{emptyText}</li>
            ) : (
              shown.map((o, i) => {
                const on = String(o.value) === String(value);
                return (
                  <li key={String(o.value)} role="option" aria-selected={on}
                    className={`${i === hi ? "hi" : ""} ${on ? "on" : ""}`}
                    onMouseEnter={() => setHi(i)} onClick={() => pick(o)}>
                    <span>{o.label}</span>
                    {on && <i className="fa-solid fa-check" />}
                  </li>
                );
              })
            )}
          </ul>
        </div>
      )}
    </div>
  );
}